'use client';

import React, { useState, useEffect } from 'react';
import Image from 'next/image';
import { motion } from 'framer-motion';
import { useTranslations } from 'next-intl';
import { SmartText, SmartTitle } from '@/components/ui/SmartFont';

// 桌面端牌位定义 - 爱心形状布局
const DESKTOP_POSITIONS = [
  { id: 1, name: 'position1', meaning: '过去爱情', xPercent: 25, yPercent: 30, rotate: -15, width: 110, height: 165, zIndex: 0 },
  { id: 2, name: 'position2', meaning: '现在爱情', xPercent: 60, yPercent: 30, rotate: 15, width: 110, height: 165, zIndex: 0 },
  { id: 3, name: 'position3', meaning: '未来爱情', xPercent: 25+17.5, yPercent: 80, rotate: 0, width: 110, height: 165, zIndex: 0 },
];

// 移动端牌位定义 - 爱心形状布局（适配小屏幕）
const MOBILE_POSITIONS = [
  { id: 1, name: 'position1', meaning: '过去爱情', xPercent: 30, yPercent: 30, rotate: -15, width: 90, height: 135, zIndex: 0 },
  { id: 2, name: 'position2', meaning: '现在爱情', xPercent: 70, yPercent: 30, rotate: 15, width: 90, height: 135, zIndex: 0 },
  { id: 3, name: 'position3', meaning: '未来爱情', xPercent: 50, yPercent: 80, rotate: 0, width: 90, height: 135, zIndex: 0 },
];

export default function LoveSpread() {
  const t = useTranslations('lovespread');
  const t1 = useTranslations('tarotcard');
  const [cards, setCards] = useState<string[]>([]);
  const [flippedCards, setFlippedCards] = useState<number[]>([]);
  const [loadedImages, setLoadedImages] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isReverseEnabled, setIsReverseEnabled] = useState(false);
  const [reversedCards, setReversedCards] = useState<number[]>([]);
  const [isAnimationComplete, setIsAnimationComplete] = useState(true);
  const [activePosition, setActivePosition] = useState<number | null>(null);
  const [isCompleted, setIsCompleted] = useState(false);
  const [visiblePositions, setVisiblePositions] = useState<number[]>([1]);
  const [positions, setPositions] = useState(DESKTOP_POSITIONS);
  const [containerSize, setContainerSize] = useState({ width: 500, height: 350 });

  // 塔罗牌列
  const tarotCards = [
    // 圣杯牌组 (Cups)
    'aceofcups', 'twoofcups', 'threeofcups', 'fourofcups', 'fiveofcups',
    'sixofcups', 'sevenofcups', 'eightofcups', 'nineofcups', 'tenofcups',
    'pageofcups', 'knightofcups', 'queenofcups', 'kingofcups',

    // 权杖牌组 (Wands)
    'aceofwands', 'twoofwands', 'threeofwands', 'fourofwands', 'fiveofwands',
    'sixofwands', 'sevenofwands', 'eightofwands', 'nineofwands', 'tenofwands',
    'pageofwands', 'knightofwands', 'queenofwands', 'kingofwands',

    // 宝剑牌组 (Swords)
    'aceofswords', 'twoofswords', 'threeofswords', 'fourofswords', 'fiveofswords',
    'sixofswords', 'sevenofswords', 'eightofswords', 'nineofswords', 'tenofswords',
    'pageofswords', 'knightofswords', 'queenofswords', 'kingofswords',

    // 钱币牌组 (Pentacles)
    'aceofpentacles', 'twoofpentacles', 'threeofpentacles', 'fourofpentacles', 'fiveofpentacles',
    'sixofpentacles', 'sevenofpentacles', 'eightofpentacles', 'nineofpentacles', 'tenofpentacles',
    'pageofpentacles', 'knightofpentacles', 'queenofpentacles', 'kingofpentacles',

    // 大阿卡纳牌组 (Major Arcana)
    'thefool', 'themagician', 'thehighpriestess', 'theempress', 'theemperor',
    'thehierophant', 'thelovers', 'thechariot', 'strength', 'thehermit',
    'wheelfortune', 'justice', 'thehangedman', 'death', 'temperance',
    'thedevil', 'thetower', 'thestar', 'themoon', 'thesun',
    'judgement', 'theworld'
  ];

  // 初始化牌阵并检测设备类型
  useEffect(() => {
    console.log('组件初始');

    // 检测设备类型并更新容器尺寸
    const checkIfMobile = () => {
      const isMobileDevice = window.innerWidth < 768;
      setPositions(isMobileDevice ? MOBILE_POSITIONS : DESKTOP_POSITIONS);
      
      // 更新容器尺寸
      if (isMobileDevice) {
        setContainerSize({ 
          width: Math.min(350, window.innerWidth - 40), // 减去左右边距
          height: 350 
        });
      } else {
        setContainerSize({ 
          width: window.innerWidth > 1024 ? 600 : 500, 
          height: 400 
        });
      }
    };

    // 初始检查
    checkIfMobile();

    // 监听窗口大小变化
    window.addEventListener('resize', checkIfMobile);

    // 初始化牌阵
    resetReading();

    // 清理监听器
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  // 获取随机卡牌，确保不重复
  const getRandomCard = (usedCards: string[]) => {
    let card;
    do {
      card = tarotCards[Math.floor(Math.random() * tarotCards.length)];
    } while (usedCards.includes(card));
    return card;
  };

  // 重置牌阵 - 接受可选参数来明确指定是否启用逆位
  const resetReading = (useReverse?: boolean) => {
    console.log('重置');
    setIsLoading(true);
    setFlippedCards([]);
    setLoadedImages([]);
    setReversedCards([]);
    setIsCompleted(false);
    setIsAnimationComplete(true);
    setVisiblePositions([1]);

    // 生成3张不重复的卡牌
    const newCards: string[] = [];
    for (let i = 0; i < 3; i++) {
      newCards.push(getRandomCard(newCards));
    }
    setCards(newCards);

    // 使用传入的参数或当前状态值来决定是否生成逆位
    const reverseEnabled = useReverse !== undefined ? useReverse : isReverseEnabled;
    
    // 生成逆位情况
    if (reverseEnabled) {
      const newReversed: number[] = [];
      for (let i = 0; i < 3; i++) {
        if (Math.random() < 0.5) {
          newReversed.push(i + 1);
        }
      }
      setReversedCards(newReversed);
    } else {
      setReversedCards([]);
    }

    // 同步设置状态
    setIsLoading(false);
    setActivePosition(1); // 直接设置不需要setTimeout
    console.log('重置完成，activePosition设置?', 1);
  };

  // 处理图片加载完成
  const handleImageLoaded = (position: number) => {
    setLoadedImages(prev => [...prev, position]);
    setIsAnimationComplete(true);

    // 添加延迟，在当前卡牌加载完成后等待一段时间再显示下一张卡牌
    if (position < 3) {
      setTimeout(() => {
        if (!visiblePositions.includes(position + 1)) {
          setVisiblePositions(prev => [...prev, position + 1]);
        }
      }, 800); // 800毫秒的延迟
    }
  };

  // 翻转卡牌
  const flipCard = (position: number) => {
    // 如果是全部翻开按钮且已完成，直接返回
    if (position === 0 && isCompleted) {
      console.log('全部已经翻开，操作被阻止');
      return;
    }

    if (
      isLoading ||
      !isAnimationComplete ||
      flippedCards.includes(position) ||
      (activePosition !== position && position !== 0)
    ) {
      console.log('翻牌被阻止，原因:');
      if (isLoading) 
        console.log('- 正在加载');
      if (!isAnimationComplete) 
        console.log('- 动画尚未完成');
      if (flippedCards.includes(position)) 
        console.log('- 此牌已经翻过');
      if (activePosition !== position && position !== 0) 
        console.log('- 不是当前激活的位置');
      return;
    }

    setIsAnimationComplete(false);

    if (position === 0) {
      // 翻转所有卡牌，无论之前是否已经翻转了一张
      const allPositions = positions.map(p => p.id);
      setFlippedCards(allPositions);
      setVisiblePositions(allPositions);
      setIsCompleted(true);
      setActivePosition(null);
    } else {
      // 翻转单张卡牌
      setFlippedCards(prev => [...prev, position]);

      // 设置下一个激活的位置
      if (position < 3) {
        setActivePosition(position + 1);
      } else {
        setIsCompleted(true);
        setActivePosition(null);
      }
    }
  };

  // 切换逆位设置
  const toggleReverse = () => {
    // 计算新的逆位设置状态
    const newReverseState = !isReverseEnabled;
    
    // 先更新状态
    setIsReverseEnabled(newReverseState);
    
    // 直接传递新状态给resetReading，不依赖组件状态
    resetReading(newReverseState);
  };

  // 计算卡牌实际位置
  const getCardPosition = (position: {id: number}) => {
    const pos = positions.find(p => p.id === position.id)!;
    // 将百分比转换为像素位置
    const x = (pos.xPercent / 100) * containerSize.width - (pos.width / 2);
    const y = (pos.yPercent / 100) * containerSize.height - (pos.height / 2);
    
    return { x, y, width: pos.width, height: pos.height, rotate: pos.rotate, zIndex: pos.zIndex };
  };
  
  // 位置标记器计算
  const getMarkerPosition = (position: number) => {
    const pos = getCardPosition({ id: position });
    const markerSize = 10; // 标记器的大小
    
    // 由于所有位置计算都相同，可以直接返回对象
      if(position === 1){
        return {
          left: `${pos.x + pos.width}px`,
          top: `${pos.y + pos.height - 2 * markerSize}px`
        }
      }
      else if(position === 2){
        return {
          left: `${pos.x + pos.width - 2.5 *markerSize}px`,
          top: `${pos.y + pos.height - 1.8 * markerSize}px`
        }
      }else{
        return {
          left: `${pos.x + pos.width - markerSize}px`,
          top: `${pos.y + pos.height - markerSize}px`
        }
      }
  };

  // 渲染牌位指示器
  const renderPositionMarker = (position: number) => {
    const isFlipped = flippedCards.includes(position);
    const markerPos = getMarkerPosition(position);

    return (
      <div
        className={`absolute text-sm font-semibold rounded-full w-6 h-6 flex items-center justify-center
          ${isFlipped ? 'bg-purple-600 text-white' : 'bg-gray-200 text-gray-800'} 
          ${position === activePosition ? 'ring-2 ring-yellow-400 animate-pulse' : ''}`}
        style={{
          left: markerPos.left,
          top: markerPos.top,
        }}
      >
        {position}
      </div>
    );
  };

  // 渲染爱心背景
  const renderHeartBackground = () => {
    const heartPos = getCardPosition({ id: 3 }); // 使用底部位置的卡牌位置
    
    return (
      <div 
        className="absolute pointer-events-none opacity-10"
        style={{
          left: `${heartPos.x - heartPos.width * 0.5}px`,
          top: `${heartPos.y - heartPos.height * 0.75}px`, // 向上移动以使卡牌在爱心底部
          width: `${heartPos.width * 2}px`,
          height: `${heartPos.height * 2}px`
        }}
      >
        <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M50 90C50 90 80 60 95 30C110 0 80 -15 50 20C20 -15 -10 0 5 30C20 60 50 90 50 90Z"
            fill="rgba(220, 38, 38, 0.5)"
          />
        </svg>
      </div>
    );
  };

  return (
    <div className="flex flex-col md:flex-row w-full gap-6 items-center md:items-start justify-between">
      {/* 左侧塔罗牌阵 */}
      <div 
        className="relative w-full mx-auto md:mx-0"
        style={{ 
          maxWidth: `${containerSize.width}px`, 
          height: `${containerSize.height}px` 
        }}
      >
        {/* 爱心背景 */}
        {renderHeartBackground()}
        
        {/* 卡片容器 - 只渲染可见的卡牌 */}
        {positions.map((position) => {
          const posCalc = getCardPosition(position);
          
          return visiblePositions.includes(position.id) && (
            <div
              key={position.id}
              className={`absolute cursor-pointer transition-transform duration-300 ease-in-out
                ${position.id === activePosition ? 'scale-[1.02]' : ''}`}
              style={{
                left: `${posCalc.x}px`,
                top: `${posCalc.y}px`,
                width: `${posCalc.width}px`,
                height: `${posCalc.height}px`,
                transform: `rotate(${posCalc.rotate}deg)`,
                zIndex: position.id === 6 ? 20 : (flippedCards.includes(position.id) ? posCalc.zIndex + 10 : posCalc.zIndex),
              }}
              onClick={() => flipCard(position.id)}
              title={`${position.id}. ${t(position.name)}`}
            >
              <div className="relative w-full h-full">
                <motion.div
                  className="w-full h-full"
                  animate={{
                    rotateY: flippedCards.includes(position.id) ? 180 : 0,
                  }}
                  transition={{ duration: 0.6, ease: "easeInOut" }}
                  style={{ transformStyle: 'preserve-3d' }}
                  onAnimationComplete={() => {
                    setIsAnimationComplete(true);
                  }}
                >
                  {/* 卡牌背面 */}
                  <div
                    className="absolute w-full h-full rounded-lg shadow-lg"
                    style={{
                      backfaceVisibility: 'hidden',
                      background: 'linear-gradient(145deg, rgba(255,255,255,0.05) 0%, rgba(0,0,0,0.1) 100%)'
                    }}
                  >
                    <Image
                      src="https://images.tarotcardgenerator.org/back.webp"
                      alt="Tarot card back"
                      fill
                      style={{
                        objectFit: 'contain',
                        borderRadius: '8px'
                      }}
                      priority
                      quality={75}
                      sizes="(max-width: 768px) 90px, 110px"
                    />
                  </div>

                  {/* 卡牌正面 */}
                  <div
                    className="absolute w-full h-full rounded-lg shadow-lg"
                    style={{
                      transform: `rotateY(180deg) ${reversedCards.includes(position.id) ? 'rotate(180deg)' : ''}`,
                      backfaceVisibility: 'hidden',
                    }}
                  >
                    {flippedCards.includes(position.id) && cards[position.id - 1] && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.3, duration: 0.3 }}
                        className="w-full h-full"
                      >
                        {!loadedImages.includes(position.id) && (
                          <motion.div
                            className="absolute inset-0"
                            animate={{ opacity: [0.5, 1, 0.5] }}
                            transition={{ duration: 1, repeat: Infinity, ease: "easeInOut" }}
                            style={{ backgroundColor: 'rgba(0, 0, 0, 0.8)' }}
                          >
                            <Image
                              src="https://images.tarotcardgenerator.org/loading.webp"
                              alt="Loading"
                              fill
                              style={{
                                objectFit: 'contain',
                                borderRadius: '8px'
                              }}
                              priority
                              quality={75}
                              sizes="110px"
                            />
                          </motion.div>
                        )}
                        <Image
                          key={cards[position.id - 1]}
                          src={`https://images.tarotcardgenerator.org/${cards[position.id - 1]}.webp`}
                          alt={t1(cards[position.id - 1])}
                          fill
                          style={{
                            objectFit: 'contain',
                            borderRadius: '8px'
                          }}
                          onLoadingComplete={() => handleImageLoaded(position.id)}
                          quality={75}
                          sizes="110px"
                        />
                      </motion.div>
                    )}
                  </div>
                </motion.div>
              </div>
            </div>
          );
        })}

        {/* 位置标记 - 只为可见的卡牌显示标记 */}
        {positions.map(position =>
          visiblePositions.includes(position.id) && renderPositionMarker(position.id)
        )}
      </div>

      {/* 替换固定高度分隔元素为更智能的解决方案 */}
      <div className="h-[80px] md:hidden w-full flex items-center justify-center">
        <div className="w-1/2 h-[1px] bg-gradient-to-r from-transparent via-red-300/30 to-transparent"></div>
      </div>

      {/* 右侧控制区域 */}
      <div className="flex flex-col gap-4 w-full md:max-w-[400px] px-4 md:pl-8 md:pr-4 lg:pl-12">
        {/* 卡牌解释 */}
        <div className="bg-black/5 backdrop-blur-md rounded-lg p-3 w-full h-full min-h-[200px] max-h-[400px] overflow-y-auto">
          <h3 className="text-lg font-bold mb-2">
            <SmartTitle>{t('interpretation')}</SmartTitle>
          </h3>

          {flippedCards.map(position => {
            const card = cards[position - 1];
            const positionData = positions.find(p => p.id === position)!;
            const isReversed = reversedCards.includes(position);

            return (
              <div key={position} className="mb-2 text-sm">
                <span className="font-bold">
                  <SmartText>{position}. {t(positionData.name)}: {t1(card)}</SmartText>
                </span> 
                {isReversed && <span className="text-red-500">
                  <SmartText> ({t('reversed')})</SmartText>
                </span>}
              </div>
            );
          })}

          {flippedCards.length === 0 && (
            <div className="text-center text-gray-500 py-4">
              <SmartText>{t('instructions')}</SmartText>
            </div>
          )}
        </div>

        {/* 控制按钮 */}
        <div className="flex flex-col sm:flex-row gap-4 w-full mb-2">
          <button
            onClick={() => flipCard(0)}
            disabled={!isAnimationComplete || isCompleted}
            className={`px-4 py-2 rounded-lg transition-all duration-300 flex-1
              ${(!isAnimationComplete || isCompleted) ? 'opacity-70 cursor-not-allowed' : 'hover:scale-[1.02] hover:shadow-[0_0_15px_rgba(220,38,38,0.6)]'}
              bg-gradient-to-r from-red-800 to-rose-700 text-white border border-red-400/30 shadow-[0_0_8px_rgba(220,38,38,0.3)]`}
          >
            <SmartText>{t('revealAll')}</SmartText>
          </button>

          <button
            onClick={toggleReverse}
            disabled={flippedCards.length > 0}
            className={`px-4 py-2 rounded-lg transition-all duration-300 flex-1
              ${flippedCards.length > 0 ? 'opacity-70 cursor-not-allowed' : 'hover:scale-[1.02] hover:shadow-[0_0_15px_rgba(220,38,38,0.6)]'}
              bg-gradient-to-r from-red-800 to-rose-700 text-white border border-red-400/30 shadow-[0_0_8px_rgba(220,38,38,0.3)]`}
          >
            <SmartText>{isReverseEnabled ? t('withReverse') : t('withoutReverse')}</SmartText>
          </button>
        </div>

        {isCompleted && (
          <button
            onClick={() => resetReading()}
            className={`px-4 py-3 rounded-lg transition-all duration-300 w-full min-h-[48px]
              bg-gradient-to-r from-pink-900 to-rose-800 text-white mb-2 border border-rose-400/30
              hover:scale-[1.02] hover:shadow-[0_0_15px_rgba(244,114,182,0.6)] shadow-[0_0_8px_rgba(244,114,182,0.3)]
              relative overflow-hidden group`}
          >
            <div className="absolute inset-0 opacity-0 group-hover:opacity-40 transition-opacity duration-500">
              <div className="absolute inset-0">
                <div className="h-2 w-2 bg-white rounded-full absolute top-1/4 left-1/4 animate-pulse shadow-glow"></div>
                <div className="h-2 w-2 bg-white rounded-full absolute top-3/4 left-1/2 animate-pulse shadow-glow"></div>
                <div className="h-2 w-2 bg-white rounded-full absolute top-1/2 left-3/4 animate-pulse shadow-glow"></div>
              </div>
            </div>
            
            <div className="absolute inset-0 opacity-10 md:hidden">
              <div className="absolute inset-0">
                <div className="h-2 w-2 bg-white rounded-full absolute top-1/4 left-1/4 shadow-glow"></div>
                <div className="h-2 w-2 bg-white rounded-full absolute top-3/4 left-1/2 shadow-glow"></div>
                <div className="h-2 w-2 bg-white rounded-full absolute top-1/2 left-3/4 shadow-glow"></div>
              </div>
            </div>
            
            <SmartText>{t('reset')}</SmartText>
          </button>
        )}

        {/* 牌阵说明 */}
        {/* <div className="bg-black/5 backdrop-blur-md rounded-lg p-3 mt-2">
          <h3 className="text-lg font-bold mb-2">
            <SmartTitle>{t('aboutLoveSpread')}</SmartTitle>
          </h3>
          <p className="text-sm">
            <SmartText>{t('loveSpreadDescription')}</SmartText>
          </p>
        </div> */}
      </div>
    </div>
  );
} 
